export type FactoryAction = 'new' | 'edit' | 'delete' | 'copy' | 'quality' | 'print' | 'mass-upload'

export type FactoryTabKind = 'summary' | 'list' | 'selector' | 'matching'

export interface FactoryColumnConfig {
  field: string
  headerName: string
  width?: number
  flex?: number
  align?: 'left' | 'right' | 'center'
  type?: 'string' | 'number' | 'date' | 'datetime'
  /** Footer totals — map sang Access `AggregateType`. `'sum'` = Access
   *  AggregateType=0; `'none'` = không có aggregate. Khi unset, default sum
   *  cho mọi `type:'number'` (giữ hành vi cũ cho tab chưa migrate). */
  aggregate?: 'sum' | 'none'
}

export interface FactorySelectorOption {
  value: string
  label: string
  accessTarget: string
  toolbarVisible: boolean
}

export interface FactoryTabConfig {
  key: string
  label: string
  accessTarget: string
  endpoint: string
  kind: FactoryTabKind
  description: string
  sqlSource?: string
  actions: FactoryAction[]
  evidence: string[]
  columns: FactoryColumnConfig[]
  sampleRows: Array<Record<string, string | number | boolean | null>>
  selectors?: FactorySelectorOption[]
  /** Khi true → tab này không xuất hiện trên top tab bar của FactoryPage;
   *  user truy cập qua Data hub (cards). */
  hiddenFromTopTabs?: boolean
  /** Bump khi đổi schema cột (Access spec migration) để invalidate localStorage
   *  + user_preferences cũ. Storage key + tabSegmentKey sẽ kèm `:v<N>` →
   *  user thấy default (all visible theo `columns`) thay vì kế thừa prefs
   *  thuộc schema cũ. */
  columnsVersion?: number
}

const numeric = { align: 'right' as const, type: 'number' as const }
/** Numeric + Access `AggregateType=0` (SUM in footer). */
const numSum = { ...numeric, aggregate: 'sum' as const }
/** Numeric + no Access aggregate — không hiển thị tổng (vd quality % bean). */
const numNoSum = { ...numeric, aggregate: 'none' as const }

export const FACTORY_TABS: FactoryTabConfig[] = [
  {
    key: 'summary',
    label: 'SUMMARY',
    accessTarget: 'frmFactorySummarySubForm2',
    endpoint: 'summary',
    kind: 'summary',
    description: 'Read-only Factory BL dashboard sourced from summary views.',
    sqlSource:
      'tlc_factory_bl_view_factory_summary_sum, tlc_factory_bl_view_factory_summary2, tlc_factory_bl_view_factory_summary3',
    actions: [],
    evidence: ['form_frmFactorySummarySubForm2.txt', 'frmFactoryQrySummary1', 'frmFactoryQrySummary2'],
    columns: [],
    sampleRows: [],
  },
  {
    key: 'stack',
    label: 'Stack',
    accessTarget: 'frmFactoryStackSubForm',
    endpoint: 'stack',
    kind: 'list',
    description: 'Stack list filtered by date_in and soft-delete status.',
    sqlSource: 'tlc_factory_bl_view_factory_stack4',
    actions: ['new', 'edit', 'delete'],
    // Bump khi gắn `aggregate` per-cột theo Access `frmFactoryQryStack` 2026-05-24
    // (TotalsRow=1, 16 cột AggregateType=0 SUM; phần còn lại không aggregate).
    // Invalidate prefs cũ qua segment `:v2`.
    columnsVersion: 2,
    evidence: ['form_frmFactoryStackSubForm.txt', 'form_frmFactoryQryStack.txt', 'query_FactoryQryStack_baoloc.txt'],
    // Column order khớp Access `ColumnOrder` 0..73 (frmFactoryQryStack).
    // `aggregate` map AggregateType: `numSum` = 0 (Sum trong dòng Totals);
    // `numNoSum` = không aggregate (cell trống ở footer).
    columns: [
      { field: 'stack_id', headerName: 'Stack ID', width: 140 },
      { field: 'stack_area', headerName: 'Stack Area (Original)', width: 150 },
      { field: 'crop', headerName: 'Crop', width: 80 },
      { field: 'warehouse', headerName: 'Warehouse', width: 150 },
      { field: 'area', headerName: 'GRN - Area', width: 120 },
      { field: 'date_in', headerName: 'Date In', width: 110, type: 'date' },
      { field: 'quality_code', headerName: 'Quality Code', width: 120 },
      { field: 'description', headerName: 'Description', width: 220 },
      { field: 'certification', headerName: 'Certification', width: 130 },
      { field: 'origin', headerName: 'Origin', width: 110 },
      { field: 'packaging', headerName: 'Package', width: 180 },
      { field: 'txt_received_bag', headerName: 'Received Bag', width: 110, ...numSum },
      { field: 'txt_received_net_w', headerName: 'Received Net Weight', width: 150, ...numSum },
      { field: 'txt_received_payment_w', headerName: 'Received Payment Weight', width: 170, ...numSum },
      { field: 'txt_issued_bag', headerName: 'Issued Bag', width: 100, ...numSum },
      { field: 'txt_issued_nw', headerName: 'Issued Net Weight', width: 140, ...numSum },
      { field: 'txt_issued_pw', headerName: 'Issue Payment Weight', width: 160, ...numSum },
      { field: 'txt_shipped_bag', headerName: 'Shipped Bag', width: 110, ...numSum },
      { field: 'txt_shipped_nw', headerName: 'Shipped Net Weight', width: 150, ...numSum },
      { field: 'txt_shipped_pw', headerName: 'Shipped Payment Weight', width: 170, ...numSum },
      { field: 'is_finished', headerName: 'Is Finished', width: 100 },
      { field: 'txt_balance_bag', headerName: 'Balance Bag', width: 110, ...numSum },
      { field: 'txt_balance_nw', headerName: 'Balance Net Weight', width: 150, ...numSum },
      { field: 'txt_balance_pw', headerName: 'Balance Payment Weight', width: 170, ...numSum },
      { field: 'txt_mc', headerName: 'MC', width: 70, ...numNoSum },
      { field: 'txt_blacks', headerName: 'Blacks', width: 80, ...numNoSum },
      { field: 'txt_brokens', headerName: 'Brokens', width: 85, ...numNoSum },
      { field: 'txt_black_and_broken', headerName: 'Black And Broken', width: 130, ...numNoSum },
      { field: 'txt_fm', headerName: 'FM', width: 70, ...numNoSum },
      { field: 'txt_mouldy', headerName: 'Mouldy', width: 85, ...numNoSum },
      { field: 'txt_brown', headerName: 'Brown', width: 80, ...numNoSum },
      { field: 'txt_spongy_beans', headerName: 'Spongy Beans', width: 110, ...numSum },
      { field: 'txt_partly_black', headerName: 'Partly Black', width: 110, ...numNoSum },
      { field: 'txt_immature', headerName: 'Immature', width: 95, ...numNoSum },
      { field: 'txt_old_crop', headerName: 'Previous Crop', width: 115, ...numSum },
      { field: 'txt_broken_over_1_per_2', headerName: 'Broken > 1/2', width: 105, ...numNoSum },
      { field: 'txt_pulper_nipped_beans', headerName: 'Pulper Nipped Beans', width: 150, ...numNoSum },
      { field: 'txt_blotchy_beans', headerName: 'Blotchy Beans', width: 115, ...numNoSum },
      { field: 'txt_waxy_beans', headerName: 'Waxy Beans', width: 105, ...numNoSum },
      { field: 'txt_insect_beans', headerName: 'Insect Beans', width: 110, ...numNoSum },
      { field: 'txt_triages_defects', headerName: 'Triages Defects', width: 125, ...numNoSum },
      { field: 'txt_fb', headerName: 'FB', width: 70, ...numNoSum },
      { field: 'txt_overdry', headerName: 'Overdry', width: 85, ...numNoSum },
      { field: 'txt_cherry', headerName: 'Cherry', width: 85, ...numNoSum },
      { field: 'txt_yellowbeans', headerName: 'Yellowbeans', width: 110, ...numNoSum },
      { field: 'txt_polish_degree', headerName: 'Polish Degree', width: 115, ...numNoSum },
      { field: 'txt_s20', headerName: 'S20', width: 70, ...numNoSum },
      { field: 'txt_s19', headerName: 'S19', width: 70, ...numNoSum },
      { field: 'txt_s18', headerName: 'S18', width: 70, ...numNoSum },
      { field: 'txt_s16', headerName: 'S16', width: 70, ...numNoSum },
      { field: 'txt_s15', headerName: 'S15', width: 70, ...numNoSum },
      { field: 'txt_s14', headerName: 'S14', width: 70, ...numNoSum },
      { field: 'txt_s13', headerName: 'S13', width: 70, ...numNoSum },
      { field: 'txt_s12', headerName: 'S12', width: 70, ...numNoSum },
      { field: 'txt_under_s12', headerName: '< S12', width: 80, ...numNoSum },
      { field: 'note', headerName: 'Note', width: 220 },
      { field: 'txt_mc_ex', headerName: 'MC Ex', width: 80, ...numNoSum },
      { field: 'txt_fm_ex', headerName: 'FM Ex', width: 80, ...numNoSum },
      { field: 'txt_bb_ex', headerName: 'BB Ex', width: 80, ...numNoSum },
      { field: 'txt_variance_mc', headerName: 'Variance MC', width: 105, ...numNoSum },
      { field: 'txt_variance_fm', headerName: 'Variance FM', width: 105, ...numNoSum },
      { field: 'txt_variance_bb', headerName: 'Variance BB', width: 105, ...numNoSum },
      { field: 'is_received_from_po', headerName: 'Is Received From PO', width: 150 },
      { field: 'stack_or_pallet', headerName: 'Stack Or Pallet', width: 130 },
      { field: 'order_no', headerName: 'Order No', width: 150 },
      { field: 'txt_real_balance_nw', headerName: 'Real balance NW', width: 140, ...numSum },
      { field: 'txt_real_balance_pw', headerName: 'Real balance PW', width: 140, ...numSum },
      { field: 'created_by', headerName: 'Created By', width: 180 },
      { field: 'created_date', headerName: 'Created Date', width: 160, type: 'datetime' },
      { field: 'updated_by', headerName: 'Updated By', width: 180 },
      { field: 'updated_date', headerName: 'Updated Date', width: 160, type: 'datetime' },
      { field: 'is_status', headerName: 'Is Status', width: 85, ...numNoSum },
      { field: 'id', headerName: 'Id', width: 80, ...numNoSum },
      { field: 'stack_area_new', headerName: 'Stack Area (Latest)', width: 150 },
    ],
    sampleRows: [],
  },
  {
    key: 'receive',
    label: 'Receive',
    accessTarget: 'frmFactoryReceiveSubForm',
    endpoint: 'receive',
    kind: 'list',
    description: 'GRN receive list with store, quality, copy and print workflows.',
    sqlSource: 'tlc_factory_bl_view_factory_receive5',
    actions: ['new', 'copy', 'edit', 'quality', 'delete', 'print'],
    // v3 (2026-05-24): gắn `aggregate` per-cột theo Access `frmFactoryQryReceive`
    // TotalsRow=1 + 10 control AggregateType=0 (SUM): gross_quantity, no_of_bag,
    // net_quantity_kg, deduction_quantity_per, deduction_quantity_kg, allowance,
    // allowance_quantity, payment_qty, txt_balance_weight, issue_to_processing.
    // Các cột number còn lại (kg_per_bag, mc, quality defects, s12..s20, is_status,
    // id) → `numNoSum` (Access không khai báo aggregate → footer trống).
    // v2 (2026-05-21): 8 → 72 cột theo spec user.
    columnsVersion: 3,
    evidence: ['form_frmFactoryReceiveSubForm.txt', 'form_frmFactoryQryReceive.txt', 'query_FactoryQryReceive_bl.txt'],
    // Column order khớp Access `ColumnOrder` 0..71 (frmFactoryQryReceive).
    // resolveListColumnConfigs ưu tiên order này; field thừa từ API (nếu có) append cuối.
    columns: [
      { field: 'grn_id', headerName: 'Grn Id', width: 150 },
      { field: 'warehouse', headerName: 'Warehouse', width: 150 },
      { field: 'date_in', headerName: 'Date In', width: 110, type: 'date' },
      { field: 'suppliers', headerName: 'Suppliers', width: 200 },
      { field: 'order_no', headerName: 'Order No', width: 130 },
      { field: 'quality_code', headerName: 'Quality Code', width: 120 },
      { field: 'description', headerName: 'Description', width: 220 },
      { field: 'category', headerName: 'Category', width: 110 },
      { field: 'certification', headerName: 'Certification', width: 130 },
      { field: 'license_cert_no', headerName: 'License Cert No', width: 140 },
      { field: 'truck_no', headerName: 'Truck No', width: 110 },
      { field: 'origin', headerName: 'Origin', width: 130 },
      { field: 'package', headerName: 'Package', width: 180 },
      { field: 'gross_quantity', headerName: 'Gross Quantity', width: 120, ...numSum },
      { field: 'kg_per_bag', headerName: 'Kg Per Bag', width: 100, ...numNoSum },
      { field: 'no_of_bag', headerName: 'No Of Bag', width: 100, ...numSum },
      { field: 'net_quantity_kg', headerName: 'Net Quantity Kg', width: 130, ...numSum },
      { field: 'mc_on_kett', headerName: 'MC on Kett', width: 100, ...numNoSum },
      { field: 'mc', headerName: 'Mc', width: 70, ...numNoSum },
      { field: 'blacks', headerName: 'Blacks', width: 80, ...numNoSum },
      { field: 'brokens', headerName: 'Brokens', width: 85, ...numNoSum },
      { field: 'black_and_broken', headerName: 'Black And Broken', width: 130, ...numNoSum },
      { field: 'fm', headerName: 'FM', width: 70, ...numNoSum },
      { field: 'mouldy', headerName: 'Mouldy', width: 85, ...numNoSum },
      { field: 'brown', headerName: 'Brown', width: 80, ...numNoSum },
      { field: 'bleach', headerName: 'Bleach', width: 80, ...numNoSum },
      { field: 'spongy_beans', headerName: 'Spongy Beans', width: 110, ...numNoSum },
      { field: 'partly_black', headerName: 'Partly Black', width: 110, ...numNoSum },
      { field: 'immature', headerName: 'Immature', width: 95, ...numNoSum },
      { field: 'old_crop', headerName: 'Previous Crop', width: 115, ...numNoSum },
      { field: 'broken_over_1_per_2', headerName: 'Broken > 1/2', width: 105, ...numNoSum },
      { field: 'pulper_nipped_beans', headerName: 'Pulper Nipped Beans', width: 150, ...numNoSum },
      { field: 'blotchy_beans', headerName: 'Blotchy Beans', width: 115, ...numNoSum },
      { field: 'waxy_beans', headerName: 'Waxy Beans', width: 105, ...numNoSum },
      { field: 'insect_beans', headerName: 'Insect Beans', width: 110, ...numNoSum },
      { field: 'triages_defects', headerName: 'Triages Defects', width: 125, ...numNoSum },
      { field: 'fb', headerName: 'FB', width: 70, ...numNoSum },
      { field: 'overdry', headerName: 'Overdry', width: 85, ...numNoSum },
      { field: 'cherry', headerName: 'Cherry', width: 85, ...numNoSum },
      { field: 'yellowbeans', headerName: 'Yellowbeans', width: 110, ...numNoSum },
      { field: 'polish_degree', headerName: 'Polish Degree', width: 115, ...numNoSum },
      { field: 's20', headerName: 'S20', width: 70, ...numNoSum },
      { field: 's19', headerName: 'S19', width: 70, ...numNoSum },
      { field: 's18', headerName: 'S18', width: 70, ...numNoSum },
      { field: 's16', headerName: 'S16', width: 70, ...numNoSum },
      { field: 's15', headerName: 'S15', width: 70, ...numNoSum },
      { field: 's14', headerName: 'S14', width: 70, ...numNoSum },
      { field: 's13', headerName: 'S13', width: 70, ...numNoSum },
      { field: 's12', headerName: 'S12', width: 70, ...numNoSum },
      { field: 'under_s12', headerName: '< S12', width: 80, ...numNoSum },
      { field: 'cuptest', headerName: 'Cuptest', width: 110 },
      { field: 'qc_name', headerName: 'QC Name', width: 130 },
      { field: 'area', headerName: 'Area', width: 110 },
      { field: 'stack_id', headerName: 'Stack ID', width: 140 },
      { field: 'deduction_quantity_per', headerName: 'Deduction (%)', width: 110, ...numSum },
      { field: 'deduction_quantity_kg', headerName: 'Deduction (Kg)', width: 120, ...numSum },
      { field: 'allowance', headerName: 'Allowance (%)', width: 110, ...numSum },
      { field: 'allowance_quantity', headerName: 'Allowance (Kg)', width: 120, ...numSum },
      { field: 'allowance_note', headerName: 'Allowance Note', width: 180 },
      { field: 'payment_qty', headerName: 'Payment Quantity', width: 140, ...numSum },
      { field: 'note', headerName: 'Note', width: 220 },
      { field: 'inspection_company', headerName: 'Inspection Company', width: 160 },
      { field: 'crop', headerName: 'Crop', width: 80 },
      { field: 'issue_to_processing', headerName: 'Issue To Processing', width: 140, ...numSum },
      { field: 'txt_balance_weight', headerName: 'Balance Weight', width: 130, ...numSum },
      { field: 'stack_or_pallet', headerName: 'Stack Or Pallet', width: 120 },
      { field: 'created_by', headerName: 'Created By', width: 180 },
      { field: 'created_date', headerName: 'Created Date', width: 160, type: 'datetime' },
      { field: 'updated_by', headerName: 'Updated By', width: 180 },
      { field: 'updated_date', headerName: 'Updated Date', width: 160, type: 'datetime' },
      { field: 'is_status', headerName: 'Is Status', width: 85, ...numNoSum },
      { field: 'id', headerName: 'Id', width: 80, ...numNoSum },
    ],
    sampleRows: [],
  },
  {
    key: 'issue',
    label: 'Issue',
    accessTarget: 'frmFactoryIssueSubForm',
    endpoint: 'issue',
    kind: 'list',
    description: 'Issue to production list with balance values calculated in the edit flow.',
    sqlSource: 'tlc_factory_bl_view_factory_issue2',
    actions: ['new', 'edit', 'delete'],
    // Bump khi gắn `aggregate` per-cột theo Access `frmFactoryQryIssue` 2026-05-24
    // (TotalsRow=1; 4 cột AggregateType=0 SUM: issued_bag, issued_nw,
    // txt_issued_pw, factory_net_weight; phần còn lại không aggregate).
    // Invalidate prefs cũ qua segment `:v3`.
    columnsVersion: 3,
    evidence: ['form_frmFactoryIssueSubForm.txt', 'form_frmFactoryQryIssue.txt', 'query_FactoryQryIssue_baoloc.txt'],
    // Column order khớp Access `ColumnOrder` 0..52 (frmFactoryQryIssue).
    // `aggregate` map AggregateType: `numSum` = 0 (Sum trong dòng Totals);
    // `numNoSum` = không aggregate (cell trống ở footer).
    columns: [
      { field: 'stack_id', headerName: 'Stack ID', width: 130 },
      { field: 'date', headerName: 'Date', width: 110, type: 'date' },
      { field: 'po_no', headerName: 'PO No', width: 150 },
      { field: 'stack_or_pallet', headerName: 'Stack Or Pallet', width: 130 },
      { field: 'warehouse', headerName: 'Warehouse', width: 140 },
      { field: 'date_in', headerName: 'Date In', width: 110, type: 'date' },
      { field: 'quality_code', headerName: 'Quality Code', width: 120 },
      { field: 'origin', headerName: 'Origin', width: 120 },
      { field: 'txt_grade', headerName: 'Grade', width: 100 },
      { field: 'txt_category', headerName: 'Category', width: 100 },
      { field: 'packaging', headerName: 'Package', width: 150 },
      { field: 'issued_bag', headerName: 'Issued Bag', width: 100, ...numSum },
      { field: 'issued_nw', headerName: 'Issued Net Weight', width: 140, ...numSum },
      { field: 'txt_issued_pw', headerName: 'Issue Payment Weight', width: 160, ...numSum },
      { field: 'note', headerName: 'Note', width: 180 },
      { field: 'mc_in', headerName: 'MC In', width: 80, ...numNoSum },
      { field: 'mc_out', headerName: 'MC Out', width: 80, ...numNoSum },
      { field: 'factory_net_weight', headerName: 'Factory Net Weight', width: 140, ...numSum },
      { field: 'txt_mc', headerName: 'MC', width: 80, ...numNoSum },
      { field: 'txt_blacks', headerName: 'Blacks', width: 80, ...numNoSum },
      { field: 'txt_brokens', headerName: 'Brokens', width: 80, ...numNoSum },
      { field: 'txt_black_and_broken', headerName: 'Black And Broken', width: 140, ...numNoSum },
      { field: 'txt_fm', headerName: 'FM', width: 70, ...numNoSum },
      { field: 'txt_mouldy', headerName: 'Mouldy', width: 80, ...numNoSum },
      { field: 'txt_brown', headerName: 'Brown', width: 80, ...numNoSum },
      { field: 'txt_partly_black', headerName: 'Partly Black', width: 110, ...numNoSum },
      { field: 'txt_immature', headerName: 'Immature', width: 100, ...numNoSum },
      { field: 'txt_broken_over_1_per_2', headerName: 'Broken > 1/2', width: 110, ...numNoSum },
      { field: 'txt_pulper_nipped_beans', headerName: 'Pulper Nipped Beans', width: 160, ...numNoSum },
      { field: 'txt_blotchy_beans', headerName: 'Blotchy Beans', width: 120, ...numNoSum },
      { field: 'txt_waxy_beans', headerName: 'Waxy Beans', width: 100, ...numNoSum },
      { field: 'txt_insect_beans', headerName: 'Insect Beans', width: 110, ...numNoSum },
      { field: 'txt_triages_defects', headerName: 'Triages Defects', width: 130, ...numNoSum },
      { field: 'txt_fb', headerName: 'FB', width: 70, ...numNoSum },
      { field: 'txt_overdry', headerName: 'Overdry', width: 80, ...numNoSum },
      { field: 'txt_cherry', headerName: 'Cherry', width: 80, ...numNoSum },
      { field: 'txt_yellowbeans', headerName: 'Yellowbeans', width: 110, ...numNoSum },
      { field: 'txt_polish_degree', headerName: 'Polish Degree', width: 120, ...numNoSum },
      { field: 'txt_s20', headerName: 'S20', width: 70, ...numNoSum },
      { field: 'txt_s19', headerName: 'S19', width: 70, ...numNoSum },
      { field: 'txt_s18', headerName: 'S18', width: 70, ...numNoSum },
      { field: 'txt_s16', headerName: 'S16', width: 70, ...numNoSum },
      { field: 'txt_s15', headerName: 'S15', width: 70, ...numNoSum },
      { field: 'txt_s14', headerName: 'S14', width: 70, ...numNoSum },
      { field: 'txt_s13', headerName: 'S13', width: 70, ...numNoSum },
      { field: 'txt_s12', headerName: 'S12', width: 70, ...numNoSum },
      { field: 'txt_under_s12', headerName: '< S12', width: 80, ...numNoSum },
      { field: 'created_by', headerName: 'Created By', width: 220 },
      { field: 'created_date', headerName: 'Created Date', width: 160, type: 'datetime' },
      { field: 'updated_by', headerName: 'Updated By', width: 220 },
      { field: 'updated_date', headerName: 'Updated Date', width: 160, type: 'datetime' },
      { field: 'is_status', headerName: 'Is Status', width: 90, ...numNoSum },
      { field: 'id', headerName: 'Id', width: 80, ...numNoSum },
    ],
    sampleRows: [],
  },
  {
    key: 'po-list',
    label: 'Po List',
    accessTarget: 'frmFactoryMainSubForm',
    endpoint: 'po-list',
    kind: 'list',
    description: 'Factory purchase-order list; po_no is editable only when creating.',
    sqlSource: 'tlc_factory_bl_view_factory_po1',
    actions: ['new', 'edit', 'delete'],
    // v2 (2026-05-24): gắn `aggregate` per-cột theo Access `frmFactoryQryPo`
    // TotalsRow=1 + 6 control AggregateType=0 (SUM): planned_quantity,
    // in_report_quantity, out_report_quantity, txt_in, txt_out, txt_wip.
    // 3 cột number còn lại (txt_unfinished, is_status, id) → `numNoSum`.
    columnsVersion: 2,
    evidence: ['form_frmFactoryMainSubForm.txt', 'form_frmFactoryQryPo.txt', 'query_FactoryQryPO_baoloc.txt'],
    // Column order khớp Access `ColumnOrder` 0..26 (frmFactoryQryPo).
    columns: [
      { field: 'po_no', headerName: 'PO No', width: 150 },
      { field: 'po_date', headerName: 'PO Date', width: 110, type: 'date' },
      { field: 'po_clossing_date', headerName: 'PO Clossing Date', width: 140, type: 'date' },
      { field: 'quality_code', headerName: 'Quality Code', width: 120 },
      { field: 'planned_quantity', headerName: 'Planned Quantity', width: 130, ...numSum },
      { field: 'in_report_quantity', headerName: 'In Report Quantity', width: 140, ...numSum },
      { field: 'out_report_quantity', headerName: 'Out Report Quantity', width: 150, ...numSum },
      { field: 'txt_in', headerName: 'In', width: 100, ...numSum },
      { field: 'txt_out', headerName: 'Out', width: 100, ...numSum },
      { field: 'is_finished', headerName: 'Is Finished', width: 100 },
      { field: 'txt_wip', headerName: 'WIP', width: 90, ...numSum },
      { field: 'processing_type', headerName: 'Processing Type', width: 140 },
      { field: 'txt_reconciled', headerName: 'Reconciled?', width: 110 },
      { field: 'bom_code', headerName: 'BOM Code', width: 110 },
      { field: 'bom_type', headerName: 'BOM Type', width: 100 },
      { field: 'bom_quality_input', headerName: 'BOM Quality Input', width: 150 },
      { field: 'bom_quality_output', headerName: 'BOM Quality Output', width: 150 },
      { field: 'note', headerName: 'Note', width: 220 },
      { field: 'txt_unfinished', headerName: 'Unfinished', width: 100, ...numNoSum },
      { field: 'quality_input', headerName: 'Quality Input', width: 130 },
      { field: 'quality_output', headerName: 'Quality Output', width: 180 },
      { field: 'created_by', headerName: 'Created By', width: 180 },
      { field: 'created_date', headerName: 'Created Date', width: 160, type: 'datetime' },
      { field: 'updated_by', headerName: 'Updated By', width: 180 },
      { field: 'updated_date', headerName: 'Updated Date', width: 160, type: 'datetime' },
      { field: 'is_status', headerName: 'Is Status', width: 85, ...numNoSum },
      { field: 'id', headerName: 'Id', width: 80, ...numNoSum },
    ],
    sampleRows: [],
  },
  {
    key: 'delivery',
    label: 'Delivery',
    accessTarget: 'frmFactoryDeliverySubForm',
    endpoint: 'delivery',
    kind: 'list',
    description: 'GDN delivery list with quality, copy and print-preview flows.',
    sqlSource: 'tlc_factory_bl_view_factory_delivery1',
    actions: ['new', 'edit', 'delete', 'copy', 'quality', 'print'],
    // v3 (2026-05-24): gắn `aggregate` per-cột theo Access `frmFactoryQryDelivery`
    // TotalsRow=1 + 9 control AggregateType=0 (SUM): number_theory_of_bag,
    // real_bags, shipments_weight, gross_weight, bags_on_truck, net_real_weight,
    // deduction_rate, txt_deducted_weight, txt_payment_weight.
    // Cột number còn lại (mc, quality defects, s12..s20, total_defect, *_at_buyer,
    // id) → `numNoSum` (Access không khai báo aggregate → footer trống).
    // v2 (2026-05-21): 8 → 80 cột theo spec user.
    columnsVersion: 3,
    evidence: ['form_frmFactoryDeliverySubForm.txt', 'form_frmFactoryQryDelivery.txt', 'query_FactoryQryDelivery_baoloc.txt'],
    // Column order khớp Access `ColumnOrder` 0..79 (frmFactoryQryDelivery).
    columns: [
      { field: 'gdn_id', headerName: 'GDN ID', width: 150 },
      { field: 'date_out', headerName: 'Date Out', width: 110, type: 'date' },
      { field: 'do_no', headerName: 'DO No', width: 120 },
      { field: 'lst_s', headerName: 'Contract No', width: 130 },
      { field: 'from_warehouse', headerName: 'From Warehouse', width: 160 },
      { field: 'to_warehouse', headerName: 'To Warehouse', width: 160 },
      { field: 'lot_name_buyer', headerName: 'Lot Name Buyer', width: 160 },
      { field: 'stack_id', headerName: 'Stack ID', width: 140 },
      { field: 'stack_quality', headerName: 'Stack Quality', width: 130 },
      { field: 'lot_id', headerName: 'Lot ID', width: 150 },
      { field: 'package', headerName: 'Package', width: 100 },
      { field: 'number_theory_of_bag', headerName: 'Number Theory Of Bag', width: 150, ...numSum },
      { field: 'real_bags', headerName: 'Real Bags', width: 100, ...numSum },
      { field: 'truck_no', headerName: 'Truck No', width: 130 },
      { field: 'package_to_truck', headerName: 'Package To Truck', width: 140 },
      { field: 'shipments_weight', headerName: 'Shipments Weight', width: 140, ...numSum },
      { field: 'gross_weight', headerName: 'Gross Weight', width: 120, ...numSum },
      { field: 'bags_on_truck', headerName: 'Bags On Truck', width: 120, ...numSum },
      { field: 'net_real_weight', headerName: 'Net Real Weight', width: 130, ...numSum },
      { field: 'deduction_rate', headerName: 'Deduction Rate', width: 130, ...numSum },
      { field: 'supervisor', headerName: 'Supervisor', width: 140 },
      { field: 'mc', headerName: 'Mc', width: 70, ...numNoSum },
      { field: 'blacks', headerName: 'Blacks', width: 80, ...numNoSum },
      { field: 'brokens', headerName: 'Brokens', width: 85, ...numNoSum },
      { field: 'black_and_broken', headerName: 'Black And Broken', width: 130, ...numNoSum },
      { field: 'fm', headerName: 'FM', width: 70, ...numNoSum },
      { field: 'mouldy', headerName: 'Mouldy', width: 85, ...numNoSum },
      { field: 'brown', headerName: 'Brown', width: 80, ...numNoSum },
      { field: 'partly_black', headerName: 'Partly Black', width: 110, ...numNoSum },
      { field: 'bleach', headerName: 'Bleach', width: 85, ...numNoSum },
      { field: 'spongy_beans', headerName: 'Spongy Beans', width: 110, ...numNoSum },
      { field: 'immature', headerName: 'Immature', width: 95, ...numNoSum },
      { field: 'old_crop', headerName: 'Previous Crop', width: 115, ...numNoSum },
      { field: 'broken_over_1_per_2', headerName: 'Broken > 1/2', width: 105, ...numNoSum },
      { field: 'pulper_nipped_beans', headerName: 'Pulper Nipped Beans', width: 150, ...numNoSum },
      { field: 'blotchy_beans', headerName: 'Blotchy Beans', width: 115, ...numNoSum },
      { field: 'waxy_beans', headerName: 'Waxy Beans', width: 105, ...numNoSum },
      { field: 'insect_beans', headerName: 'Insect Beans', width: 110, ...numNoSum },
      { field: 'triages_defects', headerName: 'Triages Defects', width: 125, ...numNoSum },
      { field: 'fb', headerName: 'FB', width: 70, ...numNoSum },
      { field: 'overdry', headerName: 'Overdry', width: 85, ...numNoSum },
      { field: 'cherry', headerName: 'Cherry', width: 85, ...numNoSum },
      { field: 'yellowbeans', headerName: 'Yellowbeans', width: 110, ...numNoSum },
      { field: 'polish_degree', headerName: 'Polish Degree', width: 115, ...numNoSum },
      { field: 's20', headerName: 'S20', width: 70, ...numNoSum },
      { field: 's19', headerName: 'S19', width: 70, ...numNoSum },
      { field: 's18', headerName: 'S18', width: 70, ...numNoSum },
      { field: 's16', headerName: 'S16', width: 70, ...numNoSum },
      { field: 's15', headerName: 'S15', width: 70, ...numNoSum },
      { field: 's14', headerName: 'S14', width: 70, ...numNoSum },
      { field: 's13', headerName: 'S13', width: 70, ...numNoSum },
      { field: 's12', headerName: 'S12', width: 70, ...numNoSum },
      { field: 'under_s12', headerName: '< S12', width: 80, ...numNoSum },
      { field: 'total_defect', headerName: 'Total Defect', width: 110, ...numNoSum },
      { field: 'cuptest', headerName: 'Cuptest', width: 110 },
      { field: 'qc_name', headerName: 'QC Name', width: 140 },
      { field: 'bag_at_buyer', headerName: 'Bag At Buyer', width: 120, ...numNoSum },
      { field: 'net_weight_at_buyer', headerName: 'Net Weight At Buyer', width: 150, ...numNoSum },
      { field: 'mc_at_buyer', headerName: 'MC At Buyer', width: 110, ...numNoSum },
      { field: 'fm_at_buyer', headerName: 'FM At Buyer', width: 110, ...numNoSum },
      { field: 'bb_at_buyer', headerName: 'BB At Buyer', width: 110, ...numNoSum },
      { field: 'mouldy_at_buyer', headerName: 'Mouldy At Buyer', width: 130, ...numNoSum },
      { field: 'white_bean_at_buyer', headerName: 'White Bean At Buyer', width: 150, ...numNoSum },
      { field: 'over_s13_at_buyer', headerName: '> S13 At Buyer', width: 130, ...numNoSum },
      { field: 'deduction_at_buyer', headerName: 'Deduction At Buyer', width: 150, ...numNoSum },
      { field: 'note', headerName: 'Note', width: 220 },
      { field: 'txt_deducted_weight', headerName: 'Deducted Weight', width: 130, ...numSum },
      { field: 'txt_payment_weight', headerName: 'Payment Weight', width: 130, ...numSum },
      { field: 'buyers_ref', headerName: 'Buyers Ref', width: 120 },
      { field: 'client', headerName: 'Client', width: 140 },
      { field: 'quality_code', headerName: 'Quality Code', width: 120 },
      { field: 'grade', headerName: 'Grade', width: 100 },
      { field: 'category', headerName: 'Category', width: 120 },
      { field: 'certification', headerName: 'Certification', width: 130 },
      { field: 'created_date', headerName: 'Created Date', width: 160, type: 'datetime' },
      { field: 'updated_date', headerName: 'Updated Date', width: 160, type: 'datetime' },
      { field: 'created_by', headerName: 'Created By', width: 180 },
      { field: 'updated_by', headerName: 'Updated By', width: 180 },
      { field: 'is_status', headerName: 'Is Status', width: 90 },
      { field: 'id', headerName: 'Id', width: 80, ...numNoSum },
    ],
    sampleRows: [
      { id: 41, gdn_id: 'BL-GDN-260502', date_out: '2026-05-02', do_no: '4776TNC', lst_s: 'S-02778', from_warehouse: 'TLC - Bao Loc', to_warehouse: 'TRIESTE, ITALY', lot_id: 'BL-LOT-2407', stack_id: 'BL-STK-2405', real_bags: 120, net_real_weight: 7200 },
      { id: 42, gdn_id: 'BL-GDN-260426', date_out: '2026-04-26', do_no: '4720HCM', lst_s: 'LS-2604-011', from_warehouse: 'TLC - Bao Loc', to_warehouse: 'HCM Port', lot_id: 'BL-LOT-2406', stack_id: 'BL-STK-2404', real_bags: 90, net_real_weight: 5400 },
    ],
  },
  {
    key: 'consumables',
    label: 'Consumables',
    accessTarget: 'frmFactoryConsumablesSubForm',
    endpoint: 'consumables',
    kind: 'list',
    description: 'MRN/MDN consumables list; copy clears MRN/MDN number and category.',
    sqlSource: 'tlc_factory_bl_view_factory_consumables1',
    hiddenFromTopTabs: true,
    actions: ['new', 'edit', 'delete', 'copy'],
    evidence: ['form_frmFactoryConsumablesSubForm.txt', 'form_frmFactoryQryConsumables.txt', 'FactoryQryConsumables_all'],
    columns: [
      { field: 'mrn_mdn_no', headerName: 'MRN/MDN', width: 130 },
      { field: 'mrn_or_mdn', headerName: 'Type', width: 90 },
      { field: 'date', headerName: 'Date', width: 120, type: 'date' },
      { field: 'category', headerName: 'Category', width: 140 },
      { field: 'item_name', headerName: 'Item', flex: 1, width: 180 },
      { field: 'qty', headerName: 'Qty', width: 90, ...numeric },
      { field: 'uom', headerName: 'UOM', width: 90 },
      { field: 'note', headerName: 'Note', flex: 1, width: 180 },
    ],
    sampleRows: [
      { id: 51, mrn_mdn_no: 'MRN-260501', mrn_or_mdn: 'MRN', date: '2026-05-01', category: 'Packaging', item_name: 'Jute bag', qty: 500, uom: 'pcs', note: 'Receipt' },
      { id: 52, mrn_mdn_no: 'MDN-260427', mrn_or_mdn: 'MDN', date: '2026-04-27', category: 'Fuel', item_name: 'Diesel', qty: 120, uom: 'litre', note: 'Line 1' },
    ],
  },
  {
    key: 'lot-allocation',
    label: 'Lot Allocation',
    accessTarget: 'frmFactoryLotAllocationSubForm',
    endpoint: 'lot-allocation',
    kind: 'list',
    description: 'Lot-to-GRN allocation list with stack and GRN balances.',
    sqlSource: 'tlc_factory_bl_view_factory_lot_allocation2',
    actions: ['new', 'edit', 'delete', 'mass-upload'],
    evidence: ['form_frmFactoryLotAllocationSubForm.txt', 'form_frmFactoryQryLotAllocation.txt', 'query_FactoryQryLotAllocation_baoloc.txt'],
    // Column order theo Access form `frmFactoryQryLotAllocation` ColumnOrder 0..44.
    // Quality fields (MC / FM / Mouldy / Brown + cls.14 Black And Broken) là
    // DLookup-from-receive (Access Name `txt_mc` / `txt_fm` / `txt_mouldy` /
    // `txt_brown` / `txt_black_and_broken`) — BE view phải precompute.
    // Cột 36 "Black And Broken" duplicate (Name `black_and_broken`) là raw
    // lot-allocation column, KHÔNG nhầm với cls.13 (txt_black_and_broken).
    // Cls.37/38: Caption Access swap với Name (`txt_bag_allocated` ↔
    // "Allocated Kg (Delivery)", `txt_kg_allocated` ↔ "Allocated Bag (Delivery)")
    // — giữ literal theo spec, không tự sửa.
    columns: [
      { field: 'lot_id', headerName: 'Lot ID', width: 140 },
      { field: 'grn_id', headerName: 'Grn Id', width: 150 },
      { field: 'bag_allocated', headerName: 'Bag Allocated', width: 120, ...numeric },
      { field: 'kg_allocated', headerName: 'Kg Allocated', width: 120, ...numeric },
      { field: 'stack_id', headerName: 'Stack ID', width: 150 },
      { field: 'area', headerName: 'GRN Area', width: 110 },
      { field: 'warehouse', headerName: 'Warehouse', width: 140 },
      { field: 'packaging', headerName: 'Package', width: 180 },
      { field: 'actual_shipped_mt', headerName: 'Actual Shipped Mt', width: 140, ...numeric },
      { field: 'quality_code', headerName: 'Quality Code', width: 120 },
      { field: 'txt_mc', headerName: 'MC', width: 80, ...numeric },
      { field: 'blacks', headerName: 'Blacks', width: 80, ...numeric },
      { field: 'brokens', headerName: 'Brokens', width: 85, ...numeric },
      { field: 'txt_black_and_broken', headerName: 'Black And Broken', width: 140, ...numeric },
      { field: 'txt_fm', headerName: 'FM', width: 70, ...numeric },
      { field: 'txt_mouldy', headerName: 'Mouldy', width: 85, ...numeric },
      { field: 'txt_brown', headerName: 'Brown', width: 80, ...numeric },
      { field: 'partly_black', headerName: 'Partly Black', width: 110, ...numeric },
      { field: 'immature', headerName: 'Immature', width: 95, ...numeric },
      { field: 'broken_over_1_per_2', headerName: 'Broken > 1/2', width: 110, ...numeric },
      { field: 'insect_beans', headerName: 'Insect Beans', width: 110, ...numeric },
      { field: 'triages_defects', headerName: 'Triages Defects', width: 130, ...numeric },
      { field: 'fb', headerName: 'FB', width: 70, ...numeric },
      { field: 'overdry', headerName: 'Overdry', width: 85, ...numeric },
      { field: 'cherry', headerName: 'Cherry', width: 85, ...numeric },
      { field: 'yellowbeans', headerName: 'Yellowbeans', width: 110, ...numeric },
      { field: 'polish_degree', headerName: 'Polish Degree', width: 120, ...numeric },
      { field: 's20', headerName: 'S20', width: 70, ...numeric },
      { field: 's19', headerName: 'S19', width: 70, ...numeric },
      { field: 's18', headerName: 'S18', width: 70, ...numeric },
      { field: 's16', headerName: 'S16', width: 70, ...numeric },
      { field: 's15', headerName: 'S15', width: 70, ...numeric },
      { field: 's14', headerName: 'S14', width: 70, ...numeric },
      { field: 's13', headerName: 'S13', width: 70, ...numeric },
      { field: 's12', headerName: 'S12', width: 70, ...numeric },
      { field: 'under_s12', headerName: '< S12', width: 80, ...numeric },
      { field: 'black_and_broken', headerName: 'Black And Broken', width: 140, ...numeric },
      { field: 'txt_bag_allocated', headerName: 'Allocated Kg (Delivery)', width: 160, ...numeric },
      { field: 'txt_kg_allocated', headerName: 'Allocated Bag (Delivery)', width: 160, ...numeric },
      { field: 'created_by', headerName: 'Created By', width: 220 },
      { field: 'created_date', headerName: 'Created Date', width: 160, type: 'datetime' },
      { field: 'updated_by', headerName: 'Updated By', width: 220 },
      { field: 'updated_date', headerName: 'Updated Date', width: 160, type: 'datetime' },
      { field: 'is_status', headerName: 'Is Status', width: 90, ...numeric },
      { field: 'id', headerName: 'Id', width: 80, ...numeric },
    ],
    sampleRows: [],
  },
  {
    key: 'lot-mgt',
    label: 'Lot Mgt',
    accessTarget: 'frmFactoryLotMgtSubForm',
    endpoint: 'lot-mgt',
    kind: 'list',
    description: 'Lot management list; new/copy suggests a fresh lot_id.',
    sqlSource: 'tlc_factory_bl_view_factory_lot_mgt1',
    // Copy kế bên New (toolbar render theo thứ tự mảng) — convention như tab receive.
    actions: ['new', 'copy', 'edit', 'delete'],
    evidence: ['form_frmFactoryLotMgtSubForm.txt', 'form_frmFactoryQryLotMgt.txt', 'query_FactoryQryLotMgt_baoloc.txt'],
    // Column order khớp danh sách chị Đẹp gửi 2026-05-27 (= view_factory_lot_mgt1
    // SELECT *). Grade group dùng prefix `txt_` (display), rate group `_rate`.
    // `numSum` = quantity/count/weight (tổng ở footer); `numNoSum` = grade/rate
    // (% không tổng). date_check chỉ dùng filter (date_col), không hiển thị.
    columns: [
      { field: 'lot_id', headerName: 'Lot Id', width: 140 },
      { field: 'lot_name', headerName: 'Lot Name', width: 130 },
      { field: 'gdn_id', headerName: 'Gdn Id', width: 120 },
      { field: 'lot_area_new', headerName: 'Lot Area (Latest)', width: 140 },
      { field: 'lot_area', headerName: 'Lot Area (Original)', width: 150 },
      { field: 'lot_name_buyer', headerName: 'Lot Name Buyer', width: 150 },
      { field: 'area', headerName: 'GRN Area', width: 120 },
      { field: 'stack_id', headerName: 'Stack Id', width: 130 },
      { field: 'contract_no', headerName: 'Contract No', width: 150 },
      { field: 'contract_quality', headerName: 'Contract Quality', width: 140 },
      { field: 'contract_client', headerName: 'Contract Client', width: 150 },
      { field: 'contract_package', headerName: 'Contract Package', width: 150 },
      { field: 'no_of_bag', headerName: 'No Of Bag', width: 100, ...numSum },
      { field: 'quantity_mt', headerName: 'Quantity KG', width: 120, ...numSum },
      { field: 'txt_balance_bag', headerName: 'Balance Bag', width: 110, ...numSum },
      { field: 'txt_balance_nw', headerName: 'Balance Nw', width: 110, ...numSum },
      { field: 'package', headerName: 'Package', width: 130 },
      { field: 'warehouse', headerName: 'Warehouse', width: 130 },
      { field: 'quality_code', headerName: 'Quality Code', width: 120 },
      { field: 'quality_description', headerName: 'Quality Description', width: 170 },
      { field: 'allocated_bag', headerName: 'Allocated Bag', width: 120, ...numSum },
      { field: 'bags_on_truck', headerName: 'Bags On Truck', width: 120, ...numSum },
      { field: 'no_of_cont', headerName: 'No Of Cont', width: 100, ...numSum },
      { field: 'net_real_weight', headerName: 'Net Real Weight', width: 130, ...numSum },
      { field: 'shipped_cont', headerName: 'Shipped Cont', width: 110, ...numSum },
      { field: 'actual_shipped_mt', headerName: 'Actual Shipped', width: 120, ...numSum },
      { field: 'txt_mc', headerName: 'Mc', width: 80, ...numNoSum },
      { field: 'txt_blacks', headerName: 'Blacks', width: 80, ...numNoSum },
      { field: 'txt_brokens', headerName: 'Brokens', width: 80, ...numNoSum },
      { field: 'txt_black_and_broken', headerName: 'Black And Broken', width: 140, ...numNoSum },
      { field: 'txt_fm', headerName: 'Fm', width: 70, ...numNoSum },
      { field: 'txt_mouldy', headerName: 'Mouldy', width: 80, ...numNoSum },
      { field: 'txt_brown', headerName: 'Brown', width: 80, ...numNoSum },
      { field: 'txt_partly_black', headerName: 'Partly Black', width: 110, ...numNoSum },
      { field: 'txt_immature', headerName: 'Immature', width: 100, ...numNoSum },
      { field: 'txt_broken_over_1_per_2', headerName: 'Broken Over 1 Per 2', width: 160, ...numNoSum },
      { field: 'txt_insect_beans', headerName: 'Insect Beans', width: 110, ...numNoSum },
      { field: 'txt_triages_defects', headerName: 'Triages Defects', width: 130, ...numNoSum },
      { field: 'txt_fb', headerName: 'Fb', width: 70, ...numNoSum },
      { field: 'txt_overdry', headerName: 'Overdry', width: 80, ...numNoSum },
      { field: 'txt_cherry', headerName: 'Cherry', width: 80, ...numNoSum },
      { field: 'txt_yellowbeans', headerName: 'Yellowbeans', width: 110, ...numNoSum },
      { field: 'txt_polish_degree', headerName: 'Polish Degree', width: 120, ...numNoSum },
      { field: 'txt_s20', headerName: 'S20', width: 70, ...numNoSum },
      { field: 'txt_s19', headerName: 'S19', width: 70, ...numNoSum },
      { field: 'txt_s18', headerName: 'S18', width: 70, ...numNoSum },
      { field: 'txt_s16', headerName: 'S16', width: 70, ...numNoSum },
      { field: 'txt_s15', headerName: 'S15', width: 70, ...numNoSum },
      { field: 'txt_s14', headerName: 'S14', width: 70, ...numNoSum },
      { field: 'txt_s13', headerName: 'S13', width: 70, ...numNoSum },
      { field: 'txt_s12', headerName: 'S12', width: 70, ...numNoSum },
      { field: 'txt_under_s12', headerName: 'Under S12', width: 90, ...numNoSum },
      { field: 'cuptest', headerName: 'Cuptest', width: 100 },
      { field: 'mc_rate', headerName: 'Mc Rate', width: 90, ...numNoSum },
      { field: 'blacks_rate', headerName: 'Blacks Rate', width: 100, ...numNoSum },
      { field: 'brokens_rate', headerName: 'Brokens Rate', width: 100, ...numNoSum },
      { field: 'black_and_broken_rate', headerName: 'Black And Broken Rate', width: 170, ...numNoSum },
      { field: 'fm_rate', headerName: 'Fm Rate', width: 90, ...numNoSum },
      { field: 'mouldy_rate', headerName: 'Mouldy Rate', width: 100, ...numNoSum },
      { field: 'brown_rate', headerName: 'Brown Rate', width: 100, ...numNoSum },
      { field: 'partly_black_rate', headerName: 'Partly Black Rate', width: 140, ...numNoSum },
      { field: 'immature_rate', headerName: 'Immature Rate', width: 120, ...numNoSum },
      { field: 'broken_over_1_per_2_rate', headerName: 'Broken Over 1 Per 2 Rate', width: 190, ...numNoSum },
      { field: 'insect_beans_rate', headerName: 'Insect Beans Rate', width: 140, ...numNoSum },
      { field: 'triages_defects_rate', headerName: 'Triages Defects Rate', width: 160, ...numNoSum },
      { field: 'fb_rate', headerName: 'Fb Rate', width: 90, ...numNoSum },
      { field: 'overdry_rate', headerName: 'Overdry Rate', width: 100, ...numNoSum },
      { field: 'cherry_rate', headerName: 'Cherry Rate', width: 100, ...numNoSum },
      { field: 'yellowbeans_rate', headerName: 'Yellowbeans Rate', width: 140, ...numNoSum },
      { field: 'polish_degree_rate', headerName: 'Polish Degree Rate', width: 150, ...numNoSum },
      { field: 's20_rate', headerName: 'S20 Rate', width: 90, ...numNoSum },
      { field: 's19_rate', headerName: 'S19 Rate', width: 90, ...numNoSum },
      { field: 's18_rate', headerName: 'S18 Rate', width: 90, ...numNoSum },
      { field: 's16_rate', headerName: 'S16 Rate', width: 90, ...numNoSum },
      { field: 's15_rate', headerName: 'S15 Rate', width: 90, ...numNoSum },
      { field: 's14_rate', headerName: 'S14 Rate', width: 90, ...numNoSum },
      { field: 's13_rate', headerName: 'S13 Rate', width: 90, ...numNoSum },
      { field: 's12_rate', headerName: 'S12 Rate', width: 90, ...numNoSum },
      { field: 'under_s12_rate', headerName: 'Under S12 Rate', width: 120, ...numNoSum },
      { field: 'date_out', headerName: 'Date Out', width: 110, type: 'date' },
      { field: 'created_by', headerName: 'Created By', width: 200 },
      { field: 'created_date', headerName: 'Created Date', width: 160, type: 'datetime' },
      { field: 'updated_by', headerName: 'Updated By', width: 200 },
      { field: 'updated_date', headerName: 'Updated Date', width: 160, type: 'datetime' },
      { field: 'is_status', headerName: 'Is Status', width: 90, ...numNoSum },
      { field: 'id', headerName: 'Id', width: 80, ...numNoSum },
    ],
    sampleRows: [],
  },
  {
    key: 'quality-standard',
    label: 'Quality Standard',
    accessTarget: 'frmFactoryDeductionStandardSubForm',
    endpoint: 'quality-standard',
    kind: 'list',
    description: 'Per-factory deduction standards — edit in deduction_standard_detail.',
    sqlSource: 'tlc_factory.deduction_standard_detail (+ join d_deduction_parameter)',
    hiddenFromTopTabs: true,
    actions: ['edit', 'delete'],
    evidence: ['form_frmFactoryDeductionStandardSubForm.txt', 'query_FactoryQryDeductionStandard_baoloc.txt'],
    columns: [
      { field: 'factory_code', headerName: 'Factory', width: 100 },
      { field: 'parameter_code', headerName: 'Parameter', width: 120 },
      { field: 'parameter_name', headerName: 'Name', flex: 1, width: 160 },
      { field: 'quality_code', headerName: 'Quality', width: 100 },
      { field: 'standard_value', headerName: 'Standard', width: 110, ...numeric },
    ],
    sampleRows: [
      { id: 81, factory_code: 'baoloc', parameter_code: 'MC', quality_code: 'R1', standard_value: 12.5, parameter_name: 'Moisture' },
      { id: 82, factory_code: 'baoloc', parameter_code: 'FM', quality_code: 'R1', standard_value: 1, parameter_name: 'Foreign Matter' },
    ],
  },
  {
    key: 'deduction-rate',
    label: 'Deduction Rate',
    accessTarget: 'frmFactoryDeductionRealSubForm',
    endpoint: 'deduction-rate',
    kind: 'list',
    description: 'Actual deduction rates — table deduction_real_detail.',
    sqlSource: 'tlc_factory.deduction_real_detail (+ join d_deduction_parameter)',
    hiddenFromTopTabs: true,
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryDeductionRealSubForm.txt', 'query_FactoryQryDeductionReal_baoloc.txt'],
    columns: [
      { field: 'factory_code', headerName: 'Factory', width: 100 },
      { field: 'parameter_code', headerName: 'Parameter', width: 120 },
      { field: 'parameter_name', headerName: 'Name', flex: 1, width: 160 },
      { field: 'field_value', headerName: 'Value', width: 90, ...numeric },
      { field: 'quality_code', headerName: 'Quality', width: 100 },
      { field: 'quality_rate', headerName: 'Rate', width: 90, ...numeric },
    ],
    sampleRows: [
      { id: 91, factory_code: 'baoloc', parameter_code: 'MC', field_value: 13, quality_code: 'R1', quality_rate: 0.5, parameter_name: 'Moisture' },
      { id: 92, factory_code: 'baoloc', parameter_code: 'FM', field_value: 2, quality_code: 'R1', quality_rate: 1, parameter_name: 'FM' },
    ],
  },
  {
    key: 'allocating-s-stack',
    label: 'Allocating: S - Stack',
    accessTarget: 'frmMatchingPSMatch_WH_Stock_Sales',
    endpoint: 'allocating-s-stack',
    kind: 'list',
    description: 'Matching workspace filtered to factory_name=baoloc.',
    sqlSource: 'public.public_v_match_s_stock',
    hiddenFromTopTabs: true,
    actions: [],
    evidence: ['form_frmMatchingPSMatch_WH_Stock_Sales.txt'],
    columns: [],
    sampleRows: [],
  },
  {
    key: 'data',
    label: 'Data',
    accessTarget: 'frmFactoryMainSubFormMD',
    endpoint: 'data',
    kind: 'selector',
    description: 'Metadata selector workspace; toolbar depends on selected dataset.',
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryMainSubFormMD.txt'],
    columns: [
      { field: 'dataset', headerName: 'Dataset', width: 180 },
      { field: 'code', headerName: 'Code', width: 140 },
      { field: 'name', headerName: 'Name', flex: 1, width: 220 },
      { field: 'status', headerName: 'Status', width: 120 },
      { field: 'updated_by', headerName: 'Updated By', width: 140 },
    ],
    selectors: [
      { value: 'item-list', label: 'Item List', accessTarget: 'frmMdQryValuation', toolbarVisible: true },
      { value: 'bom', label: 'BOM', accessTarget: 'frmFactoryMDQryBOM', toolbarVisible: true },
      { value: 'bom-cost', label: 'Batch Cost', accessTarget: 'frmFactoryMDQryBOMCost', toolbarVisible: true },
      { value: 'category-material', label: 'Category Material', accessTarget: 'frmFactoryMDQryMaterial', toolbarVisible: true },
      { value: 'movement-holidays', label: 'Movement - Holidays', accessTarget: 'frmFactoryMDQryHolidays', toolbarVisible: true },
      { value: 'movement-kpis', label: 'Movement - KPIs', accessTarget: 'frmFactoryMDQryMovementKPI', toolbarVisible: true },
      { value: 'matching-scontract', label: 'Matching - SContract', accessTarget: 'frmFactoryMDMatchingLocalS_baoloc', toolbarVisible: false },
      { value: 'matching-pcontract', label: 'Matching - PContract', accessTarget: 'frmFactoryMDMatchingLocalP_baoloc', toolbarVisible: false },
    ],
    sampleRows: [
      { id: 101, dataset: 'BOM', code: 'BOM-ROBUSTA', name: 'Robusta processing BOM', status: 'Editable', updated_by: 'system' },
      { id: 102, dataset: 'Movement - KPIs', code: 'KPI-DAILY', name: 'Daily movement target', status: 'Editable', updated_by: 'system' },
    ],
  },
  {
    key: 'report',
    label: 'Report',
    accessTarget: 'frmFactoryReport',
    endpoint: 'report',
    kind: 'selector',
    description:
      'Factory report hub — rendered as a Card grid like the Data tab; click a card to open the specific report.',
    actions: [],
    evidence: [],
    columns: [],
    sampleRows: [],
    // Movement reports — chuyển từ tab Movements sang Report 2026-05-21.
    // Batch reports — group "Batch" 2026-05-21 (chưa map Access spec cụ thể).
    selectors: [
      { value: 'report-base-goal-real', label: 'Report - Base Goal&Real', accessTarget: 'frmFactoryQryMovementReport1', toolbarVisible: false },
      { value: 'report-activities', label: 'Report - Activities', accessTarget: 'frmFactoryQryMovementReport_Movement_baoloc', toolbarVisible: false },
      { value: 'batch-report', label: 'Batch Report', accessTarget: 'frmFactoryBatchReport', toolbarVisible: false },
      { value: 'batch-comparison', label: 'Comparison', accessTarget: 'frmFactoryBatchComparison', toolbarVisible: false },
      { value: 'batch-dashboard', label: 'Dashboard', accessTarget: 'frmFactoryBatchDashboard', toolbarVisible: false },
      { value: 'batch-analysis', label: 'Analysis', accessTarget: 'frmFactoryBatchAnalysis', toolbarVisible: false },
      { value: 'batch-batch-analysis', label: 'BatchAnalysis', accessTarget: 'frmFactoryBatchBatchAnalysis', toolbarVisible: false },
    ],
  },
  {
    key: 'movements',
    label: 'Movements',
    accessTarget: 'frmFactoryMovementMainSubForm',
    endpoint: 'movements',
    kind: 'selector',
    description: 'Movement selector workspace with report modes and input modes.',
    hiddenFromTopTabs: true,
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryMovementMainSubForm.txt'],
    columns: [
      { field: 'dataset', headerName: 'Dataset', width: 190 },
      { field: 'input_date', headerName: 'Input Date', width: 120, type: 'date' },
      { field: 'lot_id', headerName: 'Lot', width: 130 },
      { field: 'stack_id', headerName: 'Stack', width: 130 },
      { field: 'moving_weight', headerName: 'Moving Wt', width: 120, ...numeric },
      { field: 'moving_bag', headerName: 'Moving Bag', width: 120, ...numeric },
      { field: 'moving_area', headerName: 'Moving Area', flex: 1, width: 160 },
    ],
    // Movement reports (`report-base-goal-real`, `report-activities`) đã
    // chuyển sang tab Report 2026-05-21.
    selectors: [
      { value: 'internal-moving', label: 'Internal Moving', accessTarget: 'frmFactoryMovementQryInternalMoving', toolbarVisible: true },
      { value: 'input-daily', label: 'Input Daily (Goal&Real)', accessTarget: 'frmFactoryMovementQryDaily_baoloc', toolbarVisible: true },
      { value: 'input-monthly', label: 'Input Monthly (Goal&Real)', accessTarget: 'frmFactoryMovementQryMonthly', toolbarVisible: true },
    ],
    sampleRows: [
      { id: 111, dataset: 'Internal Moving', input_date: '2026-05-02', lot_id: 'BL-LOT-2407', stack_id: 'BL-STK-2405', moving_weight: 7200, moving_bag: 120, moving_area: 'A01 -> Loading' },
      { id: 112, dataset: 'Input Daily (Goal&Real)', input_date: '2026-05-01', lot_id: '', stack_id: '', moving_weight: 18600, moving_bag: 310, moving_area: 'Daily input' },
    ],
  },
  // Placeholder tabs cho TRANSACTION DATA hub — render ComingSoonPage qua
  // PARKED_TAB_KEYS, hidden khỏi top tab bar (chỉ truy cập qua /data hub
  // cards). Khi rebuild xong: gỡ key khỏi PARKED_TAB_KEYS + fill columns/api.
  {
    key: 'scale-data',
    label: 'Scale Data',
    accessTarget: '',
    endpoint: 'scale-data',
    kind: 'list',
    description: 'Scale Data — coming soon.',
    hiddenFromTopTabs: true,
    actions: [],
    evidence: [],
    columns: [],
    sampleRows: [],
  },
  {
    key: 'weight-data',
    label: 'Weight Data',
    accessTarget: '',
    endpoint: 'weight-data',
    kind: 'list',
    description: 'Weight Data — coming soon.',
    hiddenFromTopTabs: true,
    actions: [],
    evidence: [],
    columns: [],
    sampleRows: [],
  },
  {
    key: 'scada-data',
    label: 'SCADA Data',
    accessTarget: '',
    endpoint: 'scada-data',
    kind: 'list',
    description: 'SCADA Data — coming soon.',
    hiddenFromTopTabs: true,
    actions: [],
    evidence: [],
    columns: [],
    sampleRows: [],
  },
]

export type FactoryTabKey = (typeof FACTORY_TABS)[number]['key']

export const DEFAULT_FACTORY_TAB = 'summary'

export function isFactoryTabKey(value: string | undefined): value is FactoryTabKey {
  return FACTORY_TABS.some((tab) => tab.key === value)
}
